import React from 'react';
import { useInvitations } from '../hooks/useInvitations';
import { useSyncStatusIndicator } from '../hooks/useSyncStatusIndicator';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { Alert, Button } from './ui';

const PendingInvitations: React.FC = () => {
  const { pending, accept, reject, error } = useInvitations();
  const { isOnline } = useSyncStatusIndicator();
  const [loadingId, setLoadingId] = React.useState<string | null>(null);

  if (pending.length === 0) return null;

  const handleAccept = async (invitationId: string) => {
    setLoadingId(invitationId);
    try {
      await accept(invitationId);
    } finally {
      setLoadingId(null);
    }
  };

  const handleReject = async (invitationId: string) => {
    setLoadingId(invitationId);
    try {
      await reject(invitationId);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="mb-6">
      {error && <Alert tone="error" className="mb-3">{error}</Alert>}

      <div className="space-y-2">
        {pending.map((invitation) => (
          <div
            key={invitation.id}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-indigo-100 bg-indigo-50 p-3 dark:border-indigo-400/30 dark:bg-indigo-500/15"
          >
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-white text-indigo-600 shadow-sm dark:bg-gray-900 dark:text-indigo-300 dark:shadow-none">
              <EnvelopeIcon className="h-5 w-5" aria-hidden="true" />
            </span>
            <p className="min-w-0 flex-1 text-sm text-gray-900 dark:text-gray-100">
              <span className="font-medium">{invitation.invited_by_name}</span> invited you to{' '}
              <span className="font-medium">“{invitation.household_name}”</span>
              {invitation.as_child && ' as a child'}
            </p>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleReject(invitation.id)}
                disabled={!!loadingId || !isOnline}
              >
                Decline
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleAccept(invitation.id)}
                loading={loadingId === invitation.id}
                disabled={!!loadingId || !isOnline}
              >
                Accept
              </Button>
            </div>
          </div>
        ))}
      </div>

      {!isOnline && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
          You must be online to accept or decline invitations.
        </p>
      )}
    </div>
  );
};

export default PendingInvitations;
